import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Search, Filter, Calendar, DollarSign, User, Building, ChevronRight, AlertCircle } from 'lucide-react';
import api from '../../api/apiClient';
import PageHeader from '../../components/PageHeader';

interface Lease {
  id: number;
  propertyAddress: string;
  propertyId: number;
  tenantName: string;
  tenantEmail: string;
  ownerName: string;
  startDate: string;
  endDate: string;
  rentAmount: number;
  rentFrequency: string;
  status: string;
  daysUntilExpiry?: number;
}

export default function AgentLeases() {
  const navigate = useNavigate();
  const [leases, setLeases] = useState<Lease[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadLeases();
  }, []);

  const loadLeases = async () => {
    setLoading(true);
    try {
      const response = await api.get<any>('/agent/leases');
      const leaseList = Array.isArray(response) ? response : (response.leases || response.content || []);
      setLeases(leaseList);
    } catch (err) {
      console.error('Failed to load leases:', err);
      setLeases([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredLeases = leases.filter(lease => {
    const matchesSearch = !searchTerm ||
      lease.propertyAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lease.tenantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lease.ownerName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || lease.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: leases.length,
    active: leases.filter(l => l.status === 'ACTIVE').length,
    expiringSoon: leases.filter(l => l.daysUntilExpiry !== undefined && l.daysUntilExpiry <= 30 && l.daysUntilExpiry > 0).length,
    expired: leases.filter(l => l.status === 'EXPIRED').length,
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE': return 'bg-green-100 text-green-800';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'EXPIRED': return 'bg-red-100 text-red-800';
      case 'TERMINATED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const formatRent = (amount: number, frequency: string) => {
    const freq = frequency?.toLowerCase() || 'week';
    return `$${amount?.toLocaleString() || 0}/${freq.replace('ly', '')}`;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <PageHeader
        title="Lease Management"
        subtitle="Manage leases for your properties"
        icon={<FileText className="w-8 h-8" />}
        backPath="/agent/dashboard"
      >
        <button
          onClick={() => navigate('/agent/leases/new')}
          className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90"
        >
          <Plus className="w-5 h-5" />
          Create Lease
        </button>
      </PageHeader>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <button
          onClick={() => setStatusFilter('all')}
          className={`bg-white rounded-lg shadow p-4 text-left hover:shadow-md transition-shadow ${statusFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Leases</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <FileText className="w-8 h-8 text-gray-200" />
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('ACTIVE')}
          className={`bg-white rounded-lg shadow p-4 text-left hover:shadow-md transition-shadow ${statusFilter === 'ACTIVE' ? 'ring-2 ring-primary' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </div>
            <Calendar className="w-8 h-8 text-green-100" />
          </div>
        </button>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Expiring Soon</p>
              <p className="text-2xl font-bold text-orange-600">{stats.expiringSoon}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-orange-100" />
          </div>
        </div>

        <button
          onClick={() => setStatusFilter('EXPIRED')}
          className={`bg-white rounded-lg shadow p-4 text-left hover:shadow-md transition-shadow ${statusFilter === 'EXPIRED' ? 'ring-2 ring-primary' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Expired</p>
              <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
            </div>
            <Calendar className="w-8 h-8 text-red-100" />
          </div>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by property, tenant, or owner..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
            >
              <option value="all">All Leases</option>
              <option value="ACTIVE">Active</option>
              <option value="PENDING">Pending</option>
              <option value="EXPIRED">Expired</option>
              <option value="TERMINATED">Terminated</option>
            </select>
          </div>
        </div>
      </div>

      {/* Leases List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {statusFilter === 'all' ? 'All Leases' : `${statusFilter} Leases`}
          </h2>
        </div>

        {filteredLeases.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No leases found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm ? 'Try adjusting your search terms.' : 'Create your first lease to get started.'}
            </p>
            <button
              onClick={() => navigate('/agent/leases/new')}
              className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90"
            >
              Create Lease
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredLeases.map(lease => (
              <div
                key={lease.id}
                className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => navigate(`/agent/leases/${lease.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(lease.status)}`}>
                        {lease.status}
                      </span>
                      {lease.daysUntilExpiry !== undefined && lease.daysUntilExpiry <= 30 && lease.daysUntilExpiry > 0 && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                          Expires in {lease.daysUntilExpiry} days
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                      <Building className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900">{lease.propertyAddress}</span>
                    </div>

                    <div className="flex items-center gap-6 text-sm text-gray-500 mb-2">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {lease.tenantName}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        {formatRent(lease.rentAmount, lease.rentFrequency)}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(lease.startDate)} - {formatDate(lease.endDate)}
                      </span>
                    </div>
                  </div>

                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


